import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from 'react';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { setSearchQuery } from '@/store/slices/uiSlice';
import { selectFile } from '@/store/slices/filesSlice';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Search, X, FileText, Hash, Clock, ArrowUpDown } from 'lucide-react';
import type { File, SearchResult, SearchMatch } from '@phrasewright/types';

interface SearchInputProps {
  className?: string;
}

export function SearchInput({ className }: SearchInputProps) {
  const dispatch = useAppDispatch();
  const { files } = useAppSelector((state) => state.files);
  const { searchQuery } = useAppSelector((state) => state.ui.fileFilters);
  const [isOpen, setIsOpen] = useState(false);
  const [localQuery, setLocalQuery] = useState(searchQuery);
  const inputRef = useRef<HTMLInputElement>(null);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );

  // Perform real-time search with debouncing
  const searchResults = useMemo(() => {
    if (!localQuery.trim() || localQuery.length < 2) {
      return [];
    }

    const query = localQuery.toLowerCase();
    const results: SearchResult[] = [];

    files.forEach((file) => {
      const matches: SearchMatch[] = [];

      // Search in file name/title
      const title = file.metadata.title || file.name;
      if (title.toLowerCase().includes(query)) {
        matches.push({
          line: 0,
          text: title,
          context: { before: '', after: '' },
        });
      }

      // Search in content
      const lines = file.content.split('\n');
      lines.forEach((line, index) => {
        if (line.toLowerCase().includes(query)) {
          const matchIndex = line.toLowerCase().indexOf(query);
          const before = line.substring(
            Math.max(0, matchIndex - 20),
            matchIndex
          );
          const after = line.substring(
            matchIndex + query.length,
            Math.min(line.length, matchIndex + query.length + 20)
          );

          matches.push({
            line: index + 1,
            text: line.substring(matchIndex, matchIndex + query.length),
            context: { before, after },
          });
        }
      });

      // Search in tags
      file.metadata.tags.forEach((tag) => {
        if (tag.toLowerCase().includes(query)) {
          matches.push({
            line: 0,
            text: tag,
            context: { before: 'tag: ', after: '' },
          });
        }
      });

      if (matches.length > 0) {
        // Calculate relevance score
        let score = 0;
        if (title.toLowerCase().includes(query)) score += 10;
        score += matches.length;
        if (file.metadata.type === 'phrase') score += 2; // Boost phrases slightly

        results.push({
          file,
          matches: matches.slice(0, 3), // Limit matches per file
          score,
        });
      }
    });

    // Sort by score (relevance) and then by file name
    return results
      .sort(
        (a, b) =>
          (b.score || 0) - (a.score || 0) ||
          (a.file?.name || '').localeCompare(b.file?.name || '')
      )
      .slice(0, 10); // Limit total results
  }, [localQuery, files]);

  // Debounced search query dispatch
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      dispatch(setSearchQuery(localQuery));
    }, 300); // 300ms debounce

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [localQuery, dispatch]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setLocalQuery(value);
      setIsOpen(value.length >= 2);
    },
    []
  );

  const handleClear = useCallback(() => {
    setLocalQuery('');
    setIsOpen(false);
    dispatch(setSearchQuery(''));
    inputRef.current?.focus();
  }, [dispatch]);

  const handleResultClick = useCallback(
    (file: File) => {
      dispatch(selectFile(file.id));
      setIsOpen(false);
      inputRef.current?.blur();
    },
    [dispatch]
  );

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      inputRef.current?.blur();
    }
  }, []);

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
    return undefined;
  }, [isOpen]);

  const getFileIcon = (file: File) => {
    return file.metadata.type === 'phrase' ? (
      <Hash className="h-4 w-4" />
    ) : (
      <FileText className="h-4 w-4" />
    );
  };

  const highlightMatch = (text: string, query: string) => {
    if (!query.trim()) return text;

    const regex = new RegExp(
      `(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`,
      'gi'
    );
    const parts = text.split(regex);

    return (
      <>
        {parts.map((part, index) =>
          regex.test(part) ? (
            <mark
              key={index}
              className="bg-yellow-200 dark:bg-yellow-900 rounded px-0.5"
            >
              {part}
            </mark>
          ) : (
            <span key={index}>{part}</span>
          )
        )}
      </>
    );
  };

  const formatFileDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className={cn('relative', className)}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          ref={inputRef}
          type="text"
          placeholder="Search files..."
          value={localQuery}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => localQuery.length >= 2 && setIsOpen(true)}
          className={cn(
            'pl-10 pr-8',
            isOpen && 'rounded-b-none border-b-transparent'
          )}
        />
        {localQuery && (
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClear}
            className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6"
          >
            <X className="h-3 w-3" />
          </Button>
        )}
      </div>

      {/* Search Results Dropdown */}
      {isOpen && searchResults.length > 0 && (
        <Card className="absolute top-full left-0 right-0 z-50 border-t-0 rounded-t-none shadow-lg">
          <CardContent className="p-0">
            <ScrollArea className="max-h-80">
              <div className="p-2">
                <div className="flex items-center gap-2 px-2 py-1 text-xs text-muted-foreground">
                  <ArrowUpDown className="h-3 w-3" />
                  <span>{searchResults.length} results</span>
                </div>
                <Separator className="my-2" />

                {searchResults.map((result, _index) => (
                  <div
                    key={result.file?.id || `result-${_index}`}
                    className="p-2 rounded-md hover:bg-accent cursor-pointer group"
                    onClick={() =>
                      result.file && handleResultClick(result.file)
                    }
                  >
                    <div className="flex items-start gap-3">
                      <div className="text-muted-foreground mt-0.5">
                        {result.file && getFileIcon(result.file)}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="text-sm font-medium truncate">
                            {result.file &&
                              highlightMatch(
                                result.file.metadata.title || result.file.name,
                                localQuery
                              )}
                          </h4>
                          {result.file &&
                            result.file.metadata.type === 'phrase' && (
                              <Badge variant="secondary" className="text-xs">
                                phrase
                              </Badge>
                            )}
                        </div>

                        {/* Show match previews */}
                        {result.matches &&
                          result.matches.map((match, matchIndex) => (
                            <div
                              key={matchIndex}
                              className="text-xs text-muted-foreground mb-1"
                            >
                              {match.line !== undefined && match.line > 0 && (
                                <span className="font-mono text-xs text-muted-foreground mr-2">
                                  L{match.line}:
                                </span>
                              )}
                              <span>
                                {match.context?.before}
                                {match.text &&
                                  highlightMatch(match.text, localQuery)}
                                {match.context?.after}
                              </span>
                            </div>
                          ))}

                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Clock className="h-3 w-3" />
                          <span>
                            {result.file &&
                              formatFileDate(result.file.updatedAt)}
                          </span>

                          {result.file &&
                            result.file.metadata.tags.length > 0 && (
                              <>
                                <span>•</span>
                                <div className="flex gap-1">
                                  {result.file &&
                                    result.file.metadata.tags
                                      .slice(0, 2)
                                      .map((tag) => (
                                        <Badge
                                          key={tag}
                                          variant="outline"
                                          className="text-xs px-1 py-0 h-4"
                                        >
                                          {highlightMatch(tag, localQuery)}
                                        </Badge>
                                      ))}
                                  {result.file.metadata.tags.length > 2 && (
                                    <Badge
                                      variant="outline"
                                      className="text-xs px-1 py-0 h-4"
                                    >
                                      +{result.file.metadata.tags.length - 2}
                                    </Badge>
                                  )}
                                </div>
                              </>
                            )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {/* No results message */}
      {isOpen && localQuery.length >= 2 && searchResults.length === 0 && (
        <Card className="absolute top-full left-0 right-0 z-50 border-t-0 rounded-t-none shadow-lg">
          <CardContent className="p-4 text-center">
            <Search className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">
              No files found for "{localQuery}"
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
